import React from 'react';

import {Icon, Text, WithTooltip} from '@albert-io/atomic';

import {TEACHER_RESTRICTION_MESSAGE} from './teacherRestriction.utils';

interface TeacherRestrictionIndicatorProps {
  className?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left' | 'right-start' | 'top-start' | 'bottom-start';
}

const TRIGGER_CLASS_NAME =
  'inline-flex cursor-default appearance-none items-center justify-center rounded-x0-half border-0 bg-transparent p-x0-half text-xs';

// 280px is intentionally arbitrary — no spacing token caps to a readable measure.
const TOOLTIP_MAX_WIDTH = 'max-w-[280px]';

/**
 * No per-school breakdown or manage CTA: teachers can't read which schools
 * restrict a question, nor change restrictions. Trigger is a real `<button>`
 * so the explanation appears on focus, not just hover.
 */
export function TeacherRestrictionIndicator({
  className,
  placement = 'right-start'
}: TeacherRestrictionIndicatorProps) {
  return (
    <WithTooltip
      as='button'
      focusable
      theme='light'
      placement={placement}
      anchoredFixedPositionProps={{
        // Portal + viewport boundaries so the tooltip isn't clipped inside
        // overflow-hidden list rows and stays beside the shield (not flipped over it).
        usePortal: true,
        modifiers: {
          preventOverflow: {boundariesElement: 'viewport'},
          flip: {boundariesElement: 'viewport'}
        }
      }}
      content={
        <div className={`text-left ${TOOLTIP_MAX_WIDTH} p-x1`}>
          <Text>{TEACHER_RESTRICTION_MESSAGE}</Text>
        </div>
      }
      wrapperProps={{
        type: 'button',
        'aria-label': 'Restricted content',
        className: className ? `${TRIGGER_CLASS_NAME} ${className}` : TRIGGER_CLASS_NAME,
        // Swallow the click so it doesn't trigger the enclosing row's navigation.
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Icon icon='shield' iconStyle='solid' color='warning' size='inherit' />
    </WithTooltip>
  );
}

export default TeacherRestrictionIndicator;
